import React, { useState, useEffect } from 'react';
import {
    User, Phone, Mail, Edit2, Save, X, Camera,
    Truck, Star, Package, LogOut, ArrowLeft, Clock, CheckCircle, XCircle, AlertCircle, Zap
} from 'lucide-react';
import './deliveryProfile.css';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';


const DeliveryBoyProfile = () => {
    const { user, logout, updateUser } = useAuth();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [updateReason, setUpdateReason] = useState('');
    const [pendingRequests, setPendingRequests] = useState([]);
    const [showRequests, setShowRequests] = useState(false);


    // Helper function to safely get profile image URL
    const getImageUrl = (imageData) => {
        if (!imageData) return '';


        // If it's an object with url property
        if (typeof imageData === 'object' && imageData.url) {
            return imageData.url;
        }


        // If it's already a string
        if (typeof imageData === 'string') {
            return imageData;
        }


        return '';
    };


    const [profileData, setProfileData] = useState({
        fullName: user?.fullName || '',
        gender: user?.gender || '',
        email: user?.email || '',
        phone: user?.phone || '',
        vehicleType: user?.vehicleType || '',
        vehicleNumber: user?.vehicleNumber || '',
        licenseNumber: user?.licenseNumber || '',
        profileImage: getImageUrl(user?.profileImage),
        totalDeliveries: user?.totalDeliveries || 0,
        avgRating: user?.ratings?.average || 0,
        totalEarnings: user?.earnings?.total || user?.earnings || 0,
        status: user?.status || 'Offline'
    });


    const [editedData, setEditedData] = useState({ ...profileData });


    const [showLogoutPopup, setShowLogoutPopup] = useState(false);


    const openLogoutPopup = () => setShowLogoutPopup(true);
    const closeLogoutPopup = () => setShowLogoutPopup(false);


    const [showPendingFlash, setShowPendingFlash] = useState(false);


    // computed flag (must be defined after pendingRequests state)
    const hasPendingRequest = pendingRequests.some(req => req.status === 'pending');



    useEffect(() => {
        if (hasPendingRequest && !isEditing) {
            setShowPendingFlash(true);


            const timer = setTimeout(() => {
                setShowPendingFlash(false);
            }, 4000); // 4000ms = 4 seconds


            return () => clearTimeout(timer);
        } else {
            // if pending cleared or user enters edit mode, hide immediately
            setShowPendingFlash(false);
        }
    }, [hasPendingRequest, isEditing]);




    const confirmLogout = () => {
        logout();
        navigate('/', { replace: true });
    };


    const [messagePopup, setMessagePopup] = useState({
        show: false,
        title: "",
        message: "",
        type: "info"  // success, error, warning
    });


    const openMessagePopup = (title, message, type = "info") => {
        setMessagePopup({
            show: true,
            title,
            message,
            type
        });


        // Auto close after 2 seconds
        setTimeout(() => {
            setMessagePopup({ show: false, title: "", message: "", type: "info" });
        }, 2000);
    };


    const closeMessagePopup = () => {
        setMessagePopup({ show: false, title: "", message: "", type: "info" });
    };




    // Fetch pending requests on component mount
    useEffect(() => {
        if (user?.id) {
            fetchPendingRequests();
        }
    }, [user]);


    const fetchPendingRequests = async () => {
        try {
            const response = await axios.get(
                `https://api.rismicmart.com/api/delivery-person/${user.id}/update-requests`
            );
            if (response.data.success) {
                setPendingRequests(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching requests:', error);
        }
    };


    const handleEditToggle = () => {
        if (isEditing) {
            setEditedData({ ...profileData });
            setSelectedFile(null);
            setFilePreview(null);
            setUpdateReason('');
        }
        setIsEditing(!isEditing);
    };


    const handleInputChange = (field, value) => {
        setEditedData(prev => ({
            ...prev,
            [field]: value
        }));
    };


    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                alert('File size should be less than 2MB');
                return;
            }


            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                alert('Only JPEG, JPG, PNG, and GIF files are allowed');
                return;
            }


            setSelectedFile(file);


            const reader = new FileReader();
            reader.onloadend = () => {
                setFilePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };


    const hasChanges = () => {
        return (
            editedData.fullName !== profileData.fullName ||
            editedData.email !== profileData.email ||
            editedData.phone !== profileData.phone ||
            selectedFile !== null
        );
    };


    const handleSave = async () => {
        if (!hasChanges()) {
            openMessagePopup(
                "No Changes Detected",
                "You haven't updated any profile fields.",
                "warning"
            );
            return;
        }


        if (!updateReason || updateReason.trim().length < 10) {
            openMessagePopup(
                "Reason Required",
                "Please provide a detailed reason (minimum 10 characters).",
                "error"
            );
            return;
        }


        setLoading(true);


        try {
            const formData = new FormData();


            if (editedData.fullName !== profileData.fullName) {
                formData.append("fullName", editedData.fullName);
            }
            if (editedData.phone !== profileData.phone) {
                formData.append("phone", editedData.phone);
            }
            if (editedData.email !== profileData.email) {
                formData.append("email", editedData.email);
            }
            if (selectedFile) {
                formData.append("profileImage", selectedFile);
            }


            formData.append("reason", updateReason.trim());


            const response = await axios.put(
                `https://api.rismicmart.com/api/delivery-person/${user.id}/request-update`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" }
                }
            );


            if (response.data.success) {
                openMessagePopup(
                    "Request Sent",
                    "Your profile update request has been sent to the admin.",
                    "success"
                );


                setIsEditing(false);
                setSelectedFile(null);
                setFilePreview(null);
                setUpdateReason('');
                setEditedData({ ...profileData });
                fetchPendingRequests();
            }
        } catch (error) {
            openMessagePopup(
                "Error",
                error.response?.data?.message || "Failed to send update request.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };


    const handleBackToDashboard = () => {
        navigate('/dashboard');
    };


    const getProfileImage = () => {
        // Priority: filePreview > profileImage URL > default avatar
        if (filePreview) {
            return filePreview;
        }


        if (profileData.profileImage) {
            // Check if profileImage is an object with url property
            if (typeof profileData.profileImage === 'object' && profileData.profileImage.url) {
                return profileData.profileImage.url;
            }


            // Check if profileImage is a string
            if (typeof profileData.profileImage === 'string') {
                // Check if it's a full URL (starts with http/https)
                if (profileData.profileImage.startsWith('http')) {
                    return profileData.profileImage;
                }
                // Otherwise it's a local path
                return `https://api.rismicmart.com${profileData.profileImage}`;
            }
        }


        // Default avatar
        return `https://ui-avatars.com/api/?name=${encodeURIComponent(profileData.fullName)}&background=007b68&color=fff&size=200`;
    };


    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return '#FFA500';
            case 'approved': return '#28a745';
            case 'rejected': return '#dc3545';
            default: return '#6c757d';
        }
    };


    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending': return <Clock size={16} />;
            case 'approved': return <CheckCircle size={16} />;
            case 'rejected': return <XCircle size={16} />;
            default: return null;
        }
    };


    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };


    // Helper to check if vehicle requires license
    const requiresLicense = (vehicleType) => {
        return vehicleType === "Bike" || vehicleType === "Car";
    };


    // Helper to get vehicle icon
    const getVehicleIcon = (vehicleType) => {
        if (vehicleType === "Battery Cycle" || vehicleType === "Battery Bike") {
            return <Zap size={16} style={{ color: '#28a745' }} />;
        }
        return <Truck size={16} />;
    };


    const currentData = isEditing ? editedData : profileData;



    return (
        <div className="delivery-boy-profile">
            <div className="profile-header-section">
                <div className="profile-header-content">
                    <button className="back-btn" onClick={handleBackToDashboard}>
                        <ArrowLeft size={20} />
                        Back to Dashboard
                    </button>
                    <h1>My Profile</h1>
                    <div className="header-actions">
                        {!isEditing ? (
                            <>
                                <button
                                    className="btn-edit"
                                    onClick={handleEditToggle}
                                    disabled={hasPendingRequest}
                                    title={hasPendingRequest ? 'You have a pending update request' : 'Edit your profile'}
                                >
                                    <Edit2 size={18} />
                                    Edit Profile
                                </button>
                                {pendingRequests.length > 0 && (
                                    <button
                                        className="btn-view-requests"
                                        onClick={() => setShowRequests(!showRequests)}
                                    >
                                        <Clock size={18} />
                                        View Requests ({pendingRequests.length})
                                    </button>
                                )}
                            </>
                        ) : (
                            <div className="edit-actions">
                                <button className="btn-cancel" onClick={handleEditToggle} disabled={loading}>
                                    <X size={18} />
                                    Cancel
                                </button>
                                <button
                                    className="btn-save"
                                    onClick={handleSave}
                                    disabled={loading || !hasChanges() || updateReason.trim().length < 10}
                                >
                                    <Save size={18} />
                                    {loading ? 'Sending Request...' : 'Send Request'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>


            


            {/* Pending Requests Section */}
            {showRequests && pendingRequests.length > 0 && (
                <div className="pending-requests-section">
                    <div className="requests-container">
                        <div className="requests-header">
                            <h3>Update Requests History</h3>
                            <button className="btn-close-requests" onClick={() => setShowRequests(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        {pendingRequests.map((request) => (
                            <div key={request._id} className="request-card">
                                <div className="request-header">
                                    <span
                                        className="request-status"
                                        style={{ color: getStatusColor(request.status) }}
                                    >
                                        {getStatusIcon(request.status)}
                                        {request.status.toUpperCase()}
                                    </span>
                                    <span className="request-date">
                                        {formatDate(request.createdAt)}
                                    </span>
                                </div>
                                <div className="request-body">
                                    <div className="request-reason">
                                        <strong>Reason:</strong>
                                        <p>{request.reason}</p>
                                    </div>
                                    <div className="requested-changes">
                                        <strong>Requested Changes:</strong>
                                        <div className="changes-list">
                                            {request.requestedChanges.fullName && (
                                                <div className="change-item">
                                                    <span className="change-label">Name:</span>
                                                    <span className="change-old">{request.currentData.fullName}</span>
                                                    <span className="change-arrow">→</span>
                                                    <span className="change-new">{request.requestedChanges.fullName}</span>
                                                </div>
                                            )}
                                            {request.requestedChanges.email && (
                                                <div className="change-item">
                                                    <span className="change-label">Email:</span>
                                                    <span className="change-old">{request.currentData.email}</span>
                                                    <span className="change-arrow">→</span>
                                                    <span className="change-new">{request.requestedChanges.email}</span>
                                                </div>
                                            )}
                                            {request.requestedChanges.phone && (
                                                <div className="change-item">
                                                    <span className="change-label">Phone:</span>
                                                    <span className="change-old">{request.currentData.phone}</span>
                                                    <span className="change-arrow">→</span>
                                                    <span className="change-new">{request.requestedChanges.phone}</span>
                                                </div>
                                            )}
                                            {request.requestedChanges.profileImage && (
                                                <div className="change-item">
                                                    <span className="change-label">Profile Image:</span>
                                                    <span className="change-new">New image uploaded</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {request.adminResponse && (
                                        <div className="admin-response">
                                            <strong>Admin Response:</strong>
                                            <p>{request.adminResponse.message}</p>
                                            {request.adminResponse.respondedByName && (
                                                <p className="responded-by">
                                                    By: {request.adminResponse.respondedByName}
                                                </p>
                                            )}
                                            <span className="response-date">
                                                Responded on {formatDate(request.adminResponse.respondedAt)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}


            <div className="profile-container">
                <div className="profile-overview-card">
                    <div className="profile-avatar-container">
                        <div className="avatar-wrapper-large">
                            <img
                                src={getProfileImage()}
                                alt="Profile"
                                className="profile-avatar-large"
                                onError={(e) => {
                                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(profileData.fullName)}&background=007b68&color=fff&size=200`;
                                }}
                            />


                            {isEditing && (
                                <label className="avatar-edit-overlay">
                                    <Camera size={24} />
                                    <span>Change Photo</span>
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/jpg,image/png,image/gif"
                                        onChange={handleImageChange}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                            )}


                            {!isEditing && (
                                <div className="online-status-badge">
                                    <div className="pulse-ring"></div>
                                    <div className="status-dot"></div>
                                </div>
                            )}
                        </div>
                        {selectedFile && (
                            <p className="file-selected-text">
                                ✓ New image selected: {selectedFile.name}
                            </p>
                        )}
                    </div>


                    <div className="profile-overview-info">
                        <h2>{currentData.fullName}</h2>
                        <p className="employee-id">{currentData.email}</p>
                        <div className="profile-quick-stats">
                            <div className="quick-stat-item">
                                <Star fill="#FFD700" stroke="#FFD700" size={20} />
                                <span>{currentData.avgRating.toFixed(1)}</span>
                            </div>
                        </div>
                    </div>
                </div>


                <div className="tab-content-wrapper">
                    <div className="tab-content">
                        {/* Update Reason Field - Show only in edit mode */}
                        {isEditing && (
                            <div className="info-section reason-section">
                                <div className="section-title">
                                    <Edit2 size={20} />
                                    <h3>Reason for Update</h3>
                                </div>
                                <div className="reason-field">
                                    <label>
                                        Why do you want to update your profile? *
                                        <span className="char-count" style={{
                                            color: updateReason.length >= 10 ? '#28a745' : '#dc3545'
                                        }}>
                                            {updateReason.length}/200
                                        </span>
                                    </label>
                                    <textarea
                                        value={updateReason}
                                        onChange={(e) => setUpdateReason(e.target.value.slice(0, 200))}
                                        className="reason-textarea"
                                        placeholder="Please provide a detailed reason for updating your profile (minimum 10 characters)..."
                                        rows="4"
                                        required
                                    />
                                    {updateReason.trim().length > 0 && updateReason.trim().length < 10 && (
                                        <p className="error-text">
                                            <AlertCircle size={14} />
                                            Reason must be at least 10 characters
                                        </p>
                                    )}
                                    {updateReason.trim().length >= 10 && (
                                        <p className="success-text">
                                            <CheckCircle size={14} />
                                            Reason is valid
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}


                        {/* Personal Information */}
                        <div className="info-section">
                            <div className="section-title">
                                <User size={20} />
                                <h3>Personal Information</h3>
                            </div>
                            <div className="info-grid">
                                <div className="info-field">
                                    <label>Full Name</label>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            value={currentData.fullName}
                                            onChange={(e) => handleInputChange('fullName', e.target.value)}
                                            className="edit-input"
                                            placeholder="Enter full name"
                                            required
                                        />
                                    ) : (
                                        <p>{currentData.fullName}</p>
                                    )}
                                </div>
                                
                                <div className="info-field">
                                    <label>Gender</label>
                                    <p>{currentData.gender || 'Not specified'}</p>
                                </div>

                                <div className="info-field">
                                    <label>
                                        <Mail size={16} /> Email Address
                                    </label>
                                    {isEditing ? (
                                        <input
                                            type="email"
                                            value={currentData.email}
                                            onChange={(e) => handleInputChange('email', e.target.value)}
                                            className="edit-input"
                                            placeholder="Enter email address"
                                            required
                                        />
                                    ) : (
                                        <p>{currentData.email}</p>
                                    )}
                                </div>
                                <div className="info-field">
                                    <label>
                                        <Phone size={16} /> Phone Number
                                    </label>
                                    {isEditing ? (
                                        <input
                                            type="tel"
                                            value={currentData.phone}
                                            onChange={(e) => handleInputChange('phone', e.target.value)}
                                            className="edit-input"
                                            placeholder="Enter phone number"
                                            pattern="[6-9]\d{9}"
                                            maxLength="10"
                                            required
                                        />
                                    ) : (
                                        <p>{currentData.phone}</p>
                                    )}
                                </div>
                            </div>
                        </div>


                        {/* Vehicle Information */}
                        <div className="info-section">
                            <div className="section-title">
                                <Truck size={20} />
                                <h3>Vehicle Information</h3>
                            </div>


                            <div className="info-grid">
                                <div className="info-field">
                                    <label>
                                        {getVehicleIcon(currentData.vehicleType)}
                                        Vehicle Type
                                    </label>
                                    <p>
                                        {currentData.vehicleType || 'N/A'}
                                        {(currentData.vehicleType === "Battery Cycle" || currentData.vehicleType === "Battery Bike") && (
                                            <span className="electric-badge">⚡ Electric</span>
                                        )}
                                    </p>
                                </div>


                                {requiresLicense(currentData.vehicleType) ? (
                                    <>
                                        <div className="info-field">
                                            <label>Vehicle Number</label>
                                            <p>{currentData.vehicleNumber || 'N/A'}</p>
                                        </div>


                                        <div className="info-field">
                                            <label>License Number</label>
                                            <p>{currentData.licenseNumber || 'N/A'}</p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="info-field">
                                            <label>Vehicle Number</label>
                                            <p className="not-required">
                                                <AlertCircle size={14} style={{ marginRight: '5px' }} />
                                                Not Required
                                            </p>
                                        </div>


                                        <div className="info-field">
                                            <label>License Number</label>
                                            <p className="not-required">
                                                <AlertCircle size={14} style={{ marginRight: '5px' }} />
                                                Not Required
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>


                        {/* Logout Button */}
                        <div className="logout-section">
                            <button className="btn-logout" onClick={openLogoutPopup}>
                                <LogOut size={18} />
                                Logout
                            </button>
                        </div>


                        {/* ====================================== */}
                        {/* 🚨 LOGOUT POPUP — Correct Position Here */}
                        {/* ====================================== */}


                        {showLogoutPopup && (
                            <div className="logout-popup-overlay">
                                <div className="logout-popup">


                                    <div className="logout-warning-icon">⚠️</div>


                                    <h2>Logout?</h2>
                                    <p>Are you sure you want to logout from your account?</p>


                                    <div className="logout-popup-buttons">
                                        <button className="btn-cancel" onClick={closeLogoutPopup}>
                                            Cancel
                                        </button>


                                        <button className="btn-confirm-logout" onClick={confirmLogout}>
                                            Logout
                                        </button>
                                    </div>


                                </div>
                            </div>
                        )}


                        {messagePopup.show && (
                            <div className="logout-popup-overlay">
                                <div className="logout-popup">


                                    <div className="logout-warning-icon">
                                        {messagePopup.type === "success" && "✅"}
                                        {messagePopup.type === "error" && "❌"}
                                        {messagePopup.type === "warning" && "⚠️"}
                                        {messagePopup.type === "info" && "ℹ️"}
                                    </div>


                                    <h2>{messagePopup.title}</h2>
                                    <p>{messagePopup.message}</p>


                                    <div className="message-popup-button">
                                        <button className="btn-confirm-logout" onClick={closeMessagePopup}>
                                            OK
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}



                        {showPendingFlash && (
                            <div className="pending-flash" role="status" aria-live="polite">
                                <AlertCircle size={18} />
                                <span className="pending-flash-text">
                                    You have a pending profile update request. Please wait for admin approval.
                                </span>
                            </div>
                        )}


                    </div>
                </div>
            </div>
        </div>
    );
};


export default DeliveryBoyProfile;